import { checkNativePlatform } from './nativeApiUtils';

// Debug logging setup
const DEBUG = process.env.NODE_ENV !== 'production';
const log = (...args) => { if (DEBUG) console.log(...args); };
const warn = (...args) => { if (DEBUG) console.warn(...args); };
const errorLog = (...args) => { if (DEBUG) console.error(...args); };

// Conditional imports for Capacitor plugins to avoid errors on web build
let Camera;
let Geolocation;
try {
  Camera = require('@capacitor/camera').Camera;
} catch (_) {
  Camera = null;
}
try {
  Geolocation = require('@capacitor/geolocation').Geolocation;
} catch (_) {
  Geolocation = null;
}

// Permission-related constants
const PERMISSIONS = {
  REQUESTED_KEY: 'sol_permissions_requested',
  STATES: {
    GRANTED: 'granted',
    DENIED: 'denied',
    PROMPT: 'prompt'
  }
};

/**
 * @typedef {Object} PermissionConfig
 * @property {boolean} enableHighAccuracy - Enable high accuracy GPS
 * @property {number} timeout - Timeout in milliseconds
 * @property {number} maximumAge - Maximum age of cached position in milliseconds
 */

/**
 * Default geolocation configuration
 * @type {PermissionConfig}
 */
const DEFAULT_CONFIG = {
  enableHighAccuracy: true,
  timeout: 5000,
  maximumAge: 0 // Force fresh permission prompt
};

/**
 * Wraps callback-based navigator.geolocation.getCurrentPosition in a Promise.
 * @param {Partial<PermissionConfig>} [config] - Optional configuration overrides
 * @returns {Promise<boolean>} True if geolocation permission was granted or prompted successfully
 */
export const requestGeolocationPermission = async (config = {}) => {
  // Merge provided config with defaults
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  // Check if geolocation is supported
  if (!('geolocation' in navigator)) {
    warn('[PermissionsUtils] Geolocation is not supported by this browser');
    return false;
  }

  // Try using the modern Permissions API first
  if ('permissions' in navigator) {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      log(`[PermissionsUtils] Current geolocation permission state: ${permissionStatus.state}`);
      
      switch (permissionStatus.state) {
        case PERMISSIONS.STATES.GRANTED:
          log('[PermissionsUtils] Geolocation permission already granted');
          return true;
          
        case PERMISSIONS.STATES.PROMPT:
          log('[PermissionsUtils] Geolocation permission requires user prompt');
          // Fall through to request permission
          break;
          
        case PERMISSIONS.STATES.DENIED:
          warn('[PermissionsUtils] Geolocation permission previously denied by user');
          return false;
          
        default:
          log(`[PermissionsUtils] Unknown permission state: ${permissionStatus.state}`);
          break;
      }
    } catch (error) {
      warn('[PermissionsUtils] Permissions API query failed, falling back to direct request:', error);
    }
  } else {
    log('[PermissionsUtils] Permissions API not supported, using direct geolocation request');
  }

  // Request geolocation permission via getCurrentPosition (wrapped in Promise)
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        log('[PermissionsUtils] Geolocation permission granted via getCurrentPosition');
        resolve(true);
      },
      (error) => {
        warn('[PermissionsUtils] Geolocation permission error:', error.message);
        resolve(false);
      },
      finalConfig
    );
  });
};

/**
 * Request location permission using Capacitor plugin with web API fallback.
 * @returns {Promise<boolean>} True if permission was granted or successfully requested
 */
const requestLocationPermission = async () => {
  let geoRequested = false;
  
  // Try Capacitor plugin first on native platforms
  if (Geolocation && typeof Geolocation.requestPermissions === 'function') {
    try {
      log('[PermissionsUtils] Requesting location permission via Capacitor plugin');
      const result = await Geolocation.requestPermissions();
      log('[PermissionsUtils] Capacitor geolocation permission result:', result);
      geoRequested = result && (result.location === 'granted' || result.coarseLocation === 'granted');
    } catch (err) {
      warn('[PermissionsUtils] Capacitor geolocation permission error:', err);
      // Fall through to web API
    }
  }
  
  // Fallback to web API if Capacitor failed or unavailable
  if (!geoRequested) {
    try {
      log('[PermissionsUtils] Falling back to web API for geolocation permission');
      geoRequested = await requestGeolocationPermission();
    } catch (webErr) {
      warn('[PermissionsUtils] Web API geolocation also failed:', webErr);
    }
  }
  
  return geoRequested;
};

/**
 * Request camera, microphone and location permissions on native platform.
 * Uses web APIs (getUserMedia/geolocation) as a fallback inside Capacitor's WebView.
 * Guarded by localStorage flag so the prompts are only shown once.
 * @returns {Promise<{camera: boolean, location: boolean}>} Permission results
 */
export const requestInitialPermissions = async () => {
  const results = { camera: false, location: false };
  
  try {
    const isNative = await checkNativePlatform();
    if (!isNative) {
      log('[PermissionsUtils] Web platform detected – skipping native permission prompts');
      return results;
    }

    const alreadyRequested = localStorage.getItem(PERMISSIONS.REQUESTED_KEY) === 'true';
    if (alreadyRequested) {
      log('[PermissionsUtils] Permissions already requested – no prompts shown');
      return results;
    }

    // CAMERA + MICROPHONE --------------------------------------------------
    if (navigator?.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        // Immediately stop tracks so we only trigger the permission prompt
        stream.getTracks().forEach(t => t.stop());
        log('[PermissionsUtils] Camera/Microphone permission requested via web API');
        results.camera = true;
      } catch (err) {
        warn('[PermissionsUtils] Camera/Microphone permission denied or error:', err);
      }
    } else if (Camera && typeof Camera.requestPermissions === 'function') {
      // Fallback to Capacitor camera plugin (rarely necessary in modern Capacitor apps)
      try {
        const result = await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
        results.camera = result && result.camera === 'granted';
      } catch (err) {
        warn('[PermissionsUtils] Camera permission error:', err);
      }
    }

    // LOCATION -------------------------------------------------------------
    results.location = await requestLocationPermission();

    // Mark as requested so we don't prompt again on next app start
    localStorage.setItem(PERMISSIONS.REQUESTED_KEY, 'true');
    log('[PermissionsUtils] Permission prompts completed:', results);
    return results;
  } catch (error) {
    errorLog('[PermissionsUtils] Error requesting permissions:', error);
    return results;
  }
};
